import {Arc} from 'd3';

import * as selectioncontextmenu from 'components/SelectionContextMenu';
import {MenuChoicesOptions} from 'components/SelectionContextMenuHelpers';
import {RingLayout} from 'molbio/maps/circular/CircularLayout';
import {getArcForStrand} from 'molbio/maps/circular/CircularUtil';
import LabeledSegmentsRingView from 'molbio/maps/circular/LabeledSegmentsRingView';
import TranslationAsAnnotation, {
  getTranslationsAsAnnotationsInRange,
} from 'molbio/translations/backbone/TranslationAsAnnotation';

/**
 * Translation view for the minimap that draws translations the same way as regular annotations.
 */
class CircularTranslationsView extends LabeledSegmentsRingView<RingLayout, TranslationAsAnnotation> {
  singularClassName = 'translation';

  static initClass(): void {
    this.prototype.className = 'translations';
    this.prototype.singularClassName = 'translation';
    this.prototype.tagName = 'g';
  }

  contextMenuData(): Array<string> {
    return selectioncontextmenu.TRANSLATION_BASE_MENU;
  }
  contextMenuOptions(d: TranslationAsAnnotation): MenuChoicesOptions {
    return {translation: d.get('translationComponentModel')};
  }
  arc(d: TranslationAsAnnotation): Arc<unknown, unknown> {
    return getArcForStrand(d.strand());
  }

  data(): Array<TranslationAsAnnotation> {
    const sequence = this.model.get('sequence');
    const {numTotal, numVisible, numShown, shownTranslations} = getTranslationsAsAnnotationsInRange(
      sequence,
      this.model.get('leftBP'),
      this.model.get('rightBP')
    );
    this.model.set({
      totalTranslations: numTotal,
      visibleTranslations: numVisible,
      shownTranslations: numShown,
    });
    return shownTranslations;
  }

  hideHandler(): void {
    if (!this.layout.get('visible')) {
      this.model.set({visibleTranslations: 0, shownTranslations: 0});
    }
  }

  clickAction(e: MouseEvent, d: TranslationAsAnnotation): void {
    d.click(this.model.get('sequence'), e.shiftKey);
  }

  dblClickAction(e: MouseEvent, d: TranslationAsAnnotation): void {
    d.dblClick(this.model.get('sequence'), e.shiftKey);
  }

  registerListeners(): void {
    super.registerListeners();
    const translations = this.model.get('sequence').translations();
    this.listenTo(translations, 'change layerChange', () => {
      this.invalidateData();
      this.triggerRedraw();
    });
    this.listenTo(translations, 'change:hover change:selected', this.handleHover.bind(this));
  }

  setConstraints(outerRadius: number): void {
    this.setOuterRadius(outerRadius);
  }

  redrawPlasmid(): boolean {
    return true;
  }

  storeBounds(rings?: number | null): void {
    if (rings == null) {
      ({rings} = this);
    }
    this.rings = rings;
    const innerRadius = this.getOuterRadius() - this.layout.get('ringHeight') * this.rings!;
    this.layout.set('innerRadius', innerRadius);
    this.updateRadius();
  }

  zeroBounds(): void {
    this.layout.set('innerRadius', this.layout.get('outerRadius'));
    this.updateRadius();
  }

  layerBounds(layer: number): {innerRadius: number; outerRadius: number} {
    return {
      innerRadius: this.radiusIn(layer + 1),
      outerRadius: this.radiusIn(layer) - this.layout.vMargin(),
    };
  }

  baseLine(layer: number): number {
    return this.radiusIn(layer + 1);
  }
}
CircularTranslationsView.initClass();

export default CircularTranslationsView;
